import { DEFAULT_PERSONAL_CHAT_NAME } from "../../../shared/mutators/createPersonalChat";
import { lastPersonalChatMessageAt } from "../../../shared/personalChatInactivity";
import { mostRecentlyActiveTab } from "./nextChatIdAfterDelete";

type Tab = {
  id: string;
  name: string;
  createdAt: number;
  lastActiveAt: number | null;
  pinnedAt: number | null;
  workflowId?: string | null;
  chat?: {
    lastChatEntryType: string | null;
    respondingAt: number | null;
    awaitingAction: boolean | null;
    entries: readonly { entryType: string; createdAt: number }[];
  };
};

export type ActiveTabAction =
  | { type: "stay" }
  | { type: "navigate"; tabId: string }
  | { type: "create" };

export function resolveActiveTab({
  tabId,
  tabs,
  pendings,
  isAdminView,
  tabsLoaded,
  hasDraft,
}: {
  tabId: string | undefined;
  tabs: readonly Tab[];
  pendings: readonly { id: string }[];
  isAdminView: boolean;
  tabsLoaded: boolean;
  hasDraft: (id: string) => boolean;
}): ActiveTabAction {
  if (tabId) {
    if (tabs.some((t) => t.id === tabId)) return { type: "stay" };
    if (!isAdminView && pendings.some((p) => p.id === tabId))
      return { type: "stay" };
  }
  if (!tabsLoaded) return { type: "stay" };
  if (isAdminView) {
    const recent = mostRecentlyActiveTab(tabs);
    return recent ? { type: "navigate", tabId: recent.id } : { type: "stay" };
  }
  if (tabId) {
    const resume = mostRecentlyActiveTab(tabs);
    if (resume) return { type: "navigate", tabId: resume.id };
  }
  const pending = pendings.find((pending) => !hasDraft(pending.id));
  if (pending) return { type: "navigate", tabId: pending.id };

  const empty = tabs
    .filter((tab) => tab.pinnedAt == null)
    .toSorted(
      (a, b) =>
        lastPersonalChatMessageAt(b.chat?.entries, b.createdAt) -
        lastPersonalChatMessageAt(a.chat?.entries, a.createdAt)
    )
    .find(
      (tab) =>
        tab.name === DEFAULT_PERSONAL_CHAT_NAME &&
        tab.workflowId == null &&
        tab.chat &&
        tab.chat.lastChatEntryType == null &&
        tab.chat.entries.length === 0 &&
        tab.chat.respondingAt == null &&
        !tab.chat.awaitingAction &&
        !hasDraft(tab.id)
    );
  if (empty) return { type: "navigate", tabId: empty.id };
  return { type: "create" };
}
